import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef
} from '@angular/core';
import { Chart } from 'chart.js';
import { SchoolModel } from '../../../models/school/school.model';
import { SchoolAdminAnalyticsService } from '../../../services/school/school-admin-analytics.service';
import {
  SchoolActivityModel,
  SchoolSkillsAnalyticsModel,
  SchoolSkillsCategoryModel,
  SchoolSkillsGradeSummaryModel
} from '../../../models/school/school-analytics.model';
import { EVOSegmentedControlItem } from '../../../shared/reusable-controls/evo-segmented-control/evo-segmented-control.component';
import {SchoolAdminService} from '../../../services/school/school-admin.service';

@Component({
  selector: 'app-school-admin-analytics',
  templateUrl: './school-admin-analytics.component.html',
  styleUrls: ['./school-admin-analytics.component.css']
})
export class SchoolAdminAnalyticsComponent implements OnInit, AfterViewInit {

  @ViewChild('masteryChart', { static: false }) masteryChartRef: ElementRef;

  schoolName = '';
  selectedGrade: number | null = null;

  segments = [
    new EVOSegmentedControlItem('All', 'All')
  ];

  overviewData: SchoolActivityModel = null;
  activationData: any = null;
  skillsData: SchoolSkillsAnalyticsModel | null = null;

  /** Lazily loaded per-grade categories — only populated in "All" mode when a grade is expanded. */
  gradeCategories: { [grade: number]: SchoolSkillsCategoryModel[] | 'loading' } = {};

  expandedGrades: { [grade: number]: boolean } = {};
  expandedCategories: { [key: string]: boolean } = {};

  private masteryChart: Chart;

  get selectedGradeLabel(): string {
    return this.selectedGrade ? `Grade ${this.selectedGrade}` : 'all grades';
  }

  /** Sorted summary grade rows — available as soon as the summary loads. */
  get summaryGrades() {
    const sd = this.skillsData;
    if (!sd || sd.mode !== 'summary') { return []; }
    return [...sd.grades].sort((a, b) => a.grade - b.grade);
  }

  constructor(private analyticsSv: SchoolAdminAnalyticsService,
              private schoolSv: SchoolAdminService) {}

  ngOnInit() {

    if (SchoolModel.getCurrent()) {
      this.schoolName = SchoolModel.getCurrent().name;
    }

    this.analyticsSv.fetchOverview().subscribe(data => this.overviewData = data);
    this.loadActivations(null);
    this.loadSkills(null);
    this.schoolSv.fetchGroupGradesSummary().subscribe((gradesSummary) => {

      console.log('grade summary', gradesSummary);

      for (const grade of gradesSummary.gradeLevelsInUse) {
        this.segments.push(
          new EVOSegmentedControlItem(`${grade}`, `Grade ${grade}`)
        );
      }

    });
  }

  ngAfterViewInit() {
    this.tryBuildMasteryChart();
  }

  loadActivations(grade: number | null) {
    this.analyticsSv.fetchActivations(undefined, { grade: grade }).subscribe(data => this.activationData = data);
  }

  /** Fetch summary (grade=null) or detail (grade set). Categories in "All" mode are lazy-loaded per grade on expand. */
  loadSkills(grade: number | null) {
    this.skillsData = null;
    this.gradeCategories = {};
    this.expandedGrades = {};
    this.expandedCategories = {};

    this.analyticsSv.fetchSkills(undefined, { grade: grade }).subscribe(data => {
      this.skillsData = data;
      if (data.mode === 'detail') {
        this.expandedGrades[data.grade] = true;
        for (const cat of data.categories) {
          this.expandedCategories[`${data.grade}-${cat.category}`] = true;
        }
      }
      this.tryBuildMasteryChart();
    });
  }

  /**
   * This currentl does nothing
   * @param item The segmented item which was clicked
   */
  onSegmentChange(item: EVOSegmentedControlItem) {

    let grade = parseInt(item.key, 10);
    if (item.key === 'All' || grade === Number.NaN) {
      grade = null;
    }

    this.selectedGrade = grade;
    this.loadActivations(grade);
    this.loadSkills(grade);
  }

  toggleGrade(grade: number) {
    this.expandedGrades[grade] = !this.expandedGrades[grade];
    // Lazy-load categories when expanding a grade in "All" mode
    if (this.expandedGrades[grade] && this.selectedGrade == null && !this.gradeCategories[grade]) {
      this.gradeCategories[grade] = 'loading';
      this.analyticsSv.fetchSkills(undefined, { grade }).subscribe(
        data => { this.gradeCategories[grade] = data.mode === 'detail' ? data.categories : []; },
        ()   => { this.gradeCategories[grade] = []; }
      );
    }
  }

  toggleCategory(grade: number, category: string) {
    const key = `${grade}-${category}`;
    this.expandedCategories[key] = !this.expandedCategories[key];
  }

  getCategoryDeltaClass(category: { coveredByPlacement: boolean; delta: number | null }): string {
    if (!category.coveredByPlacement || category.delta == null) { return 'category-delta--na'; }
    if (category.delta >= 0.2) { return 'category-delta--high'; }
    if (category.delta >= 0.1) { return 'category-delta--mid'; }
    return 'category-delta--low';
  }

  /** Same badge tiers as category headers; grade summary rows are always placement-backed. */
  getGradeSummaryDeltaClass(grade: SchoolSkillsGradeSummaryModel): string {
    return this.getCategoryDeltaClass({ coveredByPlacement: true, delta: grade.delta });
  }

  downloadCSV() {
    const pct = (v: number) => v != null ? (v * 100).toFixed(1) + '%' : '';
    const rows: string[][] = [];

    // ── Section 1: Overview totals ──────────────────────────────
    rows.push([`${SchoolModel.getCurrent().name} OVERVIEW TOTALS`]);
    rows.push(['Total Students', 'Activated', 'Activation Rate', 'Total Answers', 'Avg Answers / Student', 'Correct Rate']);
    const t = this.overviewData ? this.overviewData.totals : null;
    rows.push([
      String(t ? t.totalStudents : ''),
      String(t ? t.activatedCount : ''),
      pct(t ? t.activationRate : null),
      String(t ? t.totalAnswers : ''),
      String(t ? t.avgAnswersPerStudent : ''),
      pct(t ? t.correctRate : null)
    ]);
    rows.push([]);

    // ── Section 2: Activity by grade ────────────────────────────
    rows.push(['ACTIVITY BY GRADE']);
    rows.push(['Grade', 'Total Students', 'Activated', 'Activation Rate']);
    const byGrade = (this.overviewData && this.overviewData.byGrade) ? this.overviewData.byGrade : [];
    for (const g of byGrade) {
      rows.push([String(g.grade), String(g.totalStudents), String(g.activatedCount), pct(g.activatedRate)]);
    }
    rows.push([]);

    // ── Section 3: Activation by class ──────────────────────────
    rows.push(['ACTIVATION BY CLASS']);
    rows.push(['Class', 'Grade', 'Total Students', 'Activated', 'Activation Rate']);
    const classes = (this.activationData && this.activationData.classes) ? this.activationData.classes : [];
    for (const c of classes) {
      rows.push([c.name, String(c.grade), String(c.totalStudents), String(c.activatedCount), pct(c.activationRate)]);
    }
    rows.push([]);

    // ── Section 4: Mastery by grade (summary) ───────────────────
    if (this.skillsData && this.skillsData.mode === 'summary') {
      rows.push(['MASTERY BY GRADE']);
      rows.push(['Grade', 'Placement Baseline', 'Current Mastery', 'Delta']);
      for (const g of this.summaryGrades) {
        rows.push(['Grade ' + g.grade, pct(g.baselineMastery), pct(g.currentMastery), pct(g.delta)]);
      }
      rows.push([]);
    }

    const csv = rows.map(r => r.map(cell => '"' + String(cell).replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (this.schoolName || 'school') + '-analytics.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  /** Returns loaded categories for a grade, or null if not yet loaded / still loading. */
  categoriesForGrade(grade: number): SchoolSkillsCategoryModel[] | null {
    const c = this.gradeCategories[grade];
    return (c && c !== 'loading') ? (c as SchoolSkillsCategoryModel[]) : null;
  }

  isGradeLoading(grade: number): boolean {
    return this.gradeCategories[grade] === 'loading';
  }

  private tryBuildMasteryChart() {
    const sd = this.skillsData;
    if (!this.masteryChartRef || !sd || sd.mode !== 'summary' || !sd.grades || !sd.grades.length) { return; }
    this.buildMasteryChart();
  }

  buildMasteryChart() {
    if (!this.masteryChartRef) { return; }
    const grades = this.skillsData && this.skillsData.mode === 'summary'
      ? [...this.skillsData.grades].sort((a, b) => a.grade - b.grade)
      : [];
    if (!grades.length) { return; }

    const ctx = this.masteryChartRef.nativeElement.getContext('2d');
    if (this.masteryChart) { this.masteryChart.destroy(); }

    this.masteryChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: grades.map(g => `Grade ${g.grade}`),
        datasets: [
          {
            label: 'Placement baseline',
            data: grades.map(g => g.baselineMastery * 100),
            backgroundColor: '#009CCC',
            barPercentage: 0.7,
            categoryPercentage: 0.8
          },
          {
            label: 'Current mastery',
            data: grades.map(g => g.currentMastery * 100),
            /** #79C570 **/
            backgroundColor: '#79C570',
            barPercentage: 0.7,
            categoryPercentage: 0.8
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (context) => ` ${context.dataset.label}: ${context.parsed.y}%`
            }
          }
        },
        scales: { xAxes: [{}], yAxes: [{}] }
      }
    });
  }
}
